import React, { ReactNode } from 'react';
import {
  Input as MuiInput,
  InputProps as MuiInputProps,
  styled,
  Stack,
  useTheme
} from '@mui/material';
import Typography, { TypographyProps } from '../Typography';
import numberUtils from '@/utils/numberUtils';

interface CustomInputProps extends MuiInputProps {
  success?: boolean;
  size?: 'medium' | 'small';
  helperText?: string | ReactNode;
  titleProps?: TypographyProps;
  helperTextIcon?: ReactNode;
  helperTextProps?: TypographyProps;
  helperTextEndAdornment?: string | ReactNode;
  helperTextEndAdornmentProps?: TypographyProps;
  hasBg?: boolean;
  valueFormat?: 'numbers' | 'separatedNumbers'; // we can add other formats if needed
  valueFormatOption?: { separator?: string; separateBy?: number };
}

type OnChangeEvent = React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>;

export type InputProps = CustomInputProps;

function _handleHelperColor(
  props: InputProps,
  key: 'helperTextProps' | 'helperTextEndAdornmentProps'
) {
  if (props.error) return 'error.A100';
  if (props.success) return 'success.A100';
  return props[key]?.color || 'textColor.A60';
}

/**
 * The Input component let users enter and edit text.
 *
 * This is a custom wrapper component made of Material-UI Input.
 *
 * You can read Material-UI Input Documentation [here](https://mui.com/material-ui/api/input/).
 *
 * ```typescript
 *  import { Input } from '@rabex-kit/core';
 *  ```
 *
 *
 * @param props
 * @returns
 */

const MuiInputStyled: React.FC<MuiInputProps> = styled(MuiInput, {
  name: 'MuiInput',
  shouldForwardProp: prop =>
    ![
      'hasBg',
      'titleProps',
      'helperText',
      'helperTextIcon',
      'helperTextProps',
      'helperTextEndAdornment',
      'helperTextEndAdornmentProps',
      'success',
      'valueFormat',
      'valueFormatOption'
    ].includes(prop as string)
})<MuiInputProps>(() => ({}));

const Input: React.FC<InputProps> = props => {
  //

  const {
    palette: { mode }
  } = useTheme();

  const {
    title,
    helperTextEndAdornment,
    helperTextEndAdornmentProps,
    titleProps,
    helperText,
    helperTextIcon,
    helperTextProps,
    value,
    onChange,
    valueFormat,
    valueFormatOption
  } = props || {};

  if (!valueFormat && !!valueFormatOption) {
    console.warn("'valueFormatOption' Only Work With known 'valueFormat's...");
  }

  const _onChangeFormatHandler = (e: OnChangeEvent) => {
    let tempValue = e.target.value;

    if (valueFormat === 'numbers' || valueFormat === 'separatedNumbers') {
      tempValue = numberUtils.toEn(e.target.value).replace(/[^.\d]/g, '');
    }

    const tempEvent = { ...e, target: { ...e.target } };
    tempEvent.target.value = tempValue;
    !isNaN(tempValue as any) && onChange && onChange(tempEvent);
  };

  const _valueFormatHandler = (value: unknown) => {
    if (valueFormat === 'separatedNumbers') {
      return numberUtils.delimiter(
        value as string,
        valueFormatOption?.separator,
        valueFormatOption?.separateBy
      );
    } else return value;
  };

  return (
    <Stack
      flexDirection="column"
      gap={2}
      {...(props.fullWidth ? { width: '100%' } : {})}
    >
      {title && (
        <Typography
          color={mode === 'light' ? 'textColor.A80' : 'textColor.A30'}
          {...titleProps}
        >
          {title}
        </Typography>
      )}
      <MuiInputStyled
        {...props}
        className="amin"
        value={valueFormat ? _valueFormatHandler(value) : value}
        onChange={valueFormat ? _onChangeFormatHandler : onChange}
      />
      {(helperText || helperTextEndAdornment) && (
        <Stack flexDirection="row" justifyContent="space-between">
          {helperText && (
            <Typography
              {...helperTextProps}
              {...(props.size === 'small' && { variant: 'body3' })}
              color={_handleHelperColor(props, 'helperTextProps')}
              {...(helperTextIcon && {
                sx: {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }
              })}
            >
              {helperTextIcon && helperTextIcon}
              {helperText}
            </Typography>
          )}
          {helperTextEndAdornment && (
            <Typography
              {...helperTextEndAdornmentProps}
              {...(props.size === 'small' && { variant: 'body3' })}
              color={_handleHelperColor(props, 'helperTextEndAdornmentProps')}
            >
              {helperTextEndAdornment}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};

Input.defaultProps = {
  success: false,
  hasBg: false,
  size: 'medium',
  disableUnderline: true,
  titleProps: {
    variant: 'body2',
    weight: 'bold'
  },
  helperTextProps: {
    variant: 'body2',
    color: 'textColor.A60'
  },
  helperTextEndAdornmentProps: {
    variant: 'body2',
    color: 'textColor.A60'
  }
};

export default Input;
